import {FC, useEffect, useRef, useState} from 'react'
import {KTSVG} from '../../_metronic/helpers'
import {useDataTableStore} from '../../stores/DataTableStore'
import Swal from 'sweetalert2'
import useAccessToken from '../../hooks/AccessToken'
import {User} from '../../interfaces/User'
import {useCompanyStore} from '../../stores/CompanyStore'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {handleCaps} from '../../functions/general'
import {OTPModal} from '../OTPModal'
import {Modal} from 'bootstrap'
import useHandleCloseModal from '../../hooks/HandleCloseModal'
import { createResetPasswordSession } from '../../app/modules/auth/redux/AuthCRUD'
import useUser from '../../hooks/User'

interface Props {
  userData: User
}

const ResetPasswordUser: FC<Props> = ({userData}) => {
  const closeModalRef = useRef<HTMLButtonElement>(null)
  const user = useUser()

  const [modalBtnLoading, setModalBtnLoading] = useState(false)
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false)
  const token = useAccessToken()

  const intl = useIntl()
  const formik = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object().shape({
      password: Yup.string().required(
        intl.formatMessage(
          {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
          {title: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.PASSWORD'})}
        )
      ),
    }),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setSubmitting(true)
      setModalBtnLoading(true)
      try {
        const response = await createResetPasswordSession(userData.id, token)
        if (response.data.success) {
          const modalElement = document.getElementById(`otp-modal-reset-password`)
          if (modalElement) {
            const modalInstance = new Modal(modalElement)
            modalInstance.show()
          }
        }
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: error.response.data.message,
          confirmButtonText: 'Ok',
        })
      } finally {
        setSubmitting(false)
        setModalBtnLoading(false)
      }
    },
  })
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);
  const closeModal =()=>{
    closeModalRef.current?.click()
  }

  return (
    <>
      <div className='modal fade' tabIndex={-1} id={`reset-user-modal-${userData.id}`} ref={modalRef}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                {intl.formatMessage({id: 'FORM.ACTION.RESET_PASSWORD'})} {userData.name}
              </h5>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                onClick={handleCloseModal}
                data-bs-dismiss='modal'
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className='modal-body'>
                {/*  */}
                <>
                  <label className='form-label fs-6 fw-bolder text-dark required'>
                    {intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.PASSWORD'})}
                  </label>
                  <div className='input-group mb-3'>
                   <input
                                 type={isShowPassword ? 'text' : 'password'}
                                 placeholder='********'
                                 autoComplete='off'
                                 {...formik.getFieldProps('password')}
                                 className={clsx(
                                   'form-control form-control-lg form-control-solid txt-password',
                                   {
                                     'is-invalid': formik.touched.password && formik.errors.password,
                                   },
                                 )}
                                 aria-describedby="groupOldPassword"
                                 style={{ borderRight: "none", borderTop: "none", borderBottom: "none", borderLeft: "none", backgroundColor: "#f5f8fa" }}
                               />
                               <span className="input-group-text" id="groupOldPassword" onClick={() => setIsShowPassword(!isShowPassword)} style={{ backgroundColor: "#f5f8fa", border: "none" }}>
                                 {isShowPassword ? <i className="fas fa-eye"></i> : <i className="far fa-eye-slash"></i>}
                               </span>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container text-danger'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.password}
                      </span>
                    </div>
                  )}
                </>{' '}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  ref={closeModalRef}
                  data-bs-dismiss='modal'
                >
                  {intl.formatMessage({id: 'FORM.ACTION.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className={`btn btn-danger me-10 ${modalBtnLoading ? 'disabled' : ''}`}
                  data-kt-indicator={modalBtnLoading ? 'on' : 'off'}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'FORM.ACTION.RESET_PASSWORD'})}
                  </span>
                  <span className='indicator-progress'>
                    Loading...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <OTPModal
        username={userData.username}
        password={formik.values.password}
        phone={user.data.phone}
        type={'reset-password'}
        userId={userData.id}
        callback={closeModal}
      />
    </>
  )
}

export {ResetPasswordUser}
